import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { logWarn } from '../lib/logger';

const DISMISS_KEY = 'taylord_banner_dismissed_at';

const withinRange = (b) => {
  if (!b?.enabled || !b?.message) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (b.starts_at) {
    const s = new Date(b.starts_at);
    if (!Number.isNaN(s.getTime()) && today < s) return false;
  }
  if (b.ends_at) {
    const e = new Date(b.ends_at);
    e.setHours(23, 59, 59, 999);
    if (!Number.isNaN(e.getTime()) && today > e) return false;
  }
  return true;
};

const SiteBanner = () => {
  const [banner, setBanner] = useState(null);
  const [dismissedFor, setDismissedFor] = useState(null);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/banner`);
        if (!cancelled) setBanner(data);
      } catch (err) {
        // Banner is non-critical — log and move on
        logWarn('Banner fetch failed:', err?.message || err);
      }
    })();

    try {
      setDismissedFor(sessionStorage.getItem(DISMISS_KEY));
    } catch (err) {
      logWarn('sessionStorage unavailable for banner dismiss:', err?.message || err);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Mount-only effect; local vars (cancelled, data) are not reactive deps

  const shouldShow = withinRange(banner);
  // Homepage-only scope: only render on '/'
  const scopeOk = banner?.scope !== 'homepage_only' || location.pathname === '/';
  const bannerKey = banner?.updated_at || banner?.message || '';
  const visible = shouldShow && scopeOk && dismissedFor !== bannerKey;

  // Publish banner height as a CSS var so fixed Header can sit below it
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.style.setProperty('--site-banner-h', '0px');
      return undefined;
    }
    const apply = () => {
      const h = ref.current?.offsetHeight || 0;
      root.style.setProperty('--site-banner-h', `${h}px`);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      root.style.setProperty('--site-banner-h', '0px');
    };
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, bannerKey); }
    catch (err) { logWarn('Failed to persist banner dismiss:', err?.message || err); }
    setDismissedFor(bannerKey);
  };

  return (
    <div
      ref={ref}
      data-testid="site-banner"
      className="fixed top-0 left-0 right-0 z-[10000] w-full bg-gradient-to-r from-red-700 via-red-600 to-red-700 text-white shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm sm:text-[15px] font-medium">
          <span className="hidden sm:inline-block h-2 w-2 rounded-full bg-white animate-pulse" aria-hidden />
          <span data-testid="site-banner-message">{banner.message}</span>
          {banner.link_text && banner.link_url && (
            <a
              href={banner.link_url}
              data-testid="site-banner-link"
              className="ml-2 underline underline-offset-2 hover:text-red-100 text-sm font-semibold"
            >
              {banner.link_text}
            </a>
          )}
        </div>
        <button
          data-testid="site-banner-dismiss"
          aria-label="Dismiss banner"
          onClick={dismiss}
          className="shrink-0 w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center transition"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default SiteBanner;
